/**
 * Form card — the grid tile for a form: status rail, category icon, entry counts,
 * closing countdown, short link and a quick-actions menu.
 */

import type { ReactNode } from 'react';
import {
  IconCalendarEvent,
  IconSchool,
  IconBabyCarriage,
  IconHeartHandshake,
  IconMessageDots,
  IconForms,
  IconDotsVertical,
  IconClock,
  IconAlertTriangle,
  IconLock,
} from '@tabler/icons-react';
import type { FormListItemView } from '@actschurch/ccms-sdk';
import { Card, Group, Stack, Text, ThemeIcon, Badge, Menu, Tooltip, Box, SimpleGrid } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { FormStatusBadge } from './FormsBadges';
import { RefChip } from './FormsUi';
import { FORM_STATUS_META, closingInfo } from './formsDisplay';

const CATEGORY_ICONS: { match: RegExp; icon: (s: number) => ReactNode; color: string }[] = [
  { match: /event|conference|camp|summit/i, icon: (s) => <IconCalendarEvent size={s} />, color: 'blue' },
  { match: /course|class|school|training/i, icon: (s) => <IconSchool size={s} />, color: 'indigo' },
  { match: /dedicat|baby|baptis/i, icon: (s) => <IconBabyCarriage size={s} />, color: 'pink' },
  { match: /volunt|serve|team/i, icon: (s) => <IconHeartHandshake size={s} />, color: 'teal' },
  { match: /survey|feedback|poll/i, icon: (s) => <IconMessageDots size={s} />, color: 'orange' },
];

export function formVisual(form: Pick<FormListItemView, 'categoryName' | 'name'>, size = 20): { icon: ReactNode; color: string } {
  const hay = `${form.categoryName ?? ''} ${form.name ?? ''}`;
  const hit = CATEGORY_ICONS.find((c) => c.match.test(hay));
  return hit ? { icon: hit.icon(size), color: hit.color } : { icon: <IconForms size={size} />, color: 'violet' };
}

function Count({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Stack gap={0} align="flex-start">
      <Text size="lg" fw={700} c={value > 0 ? color : 'dimmed'} lh={1.2}>
        {value.toLocaleString()}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  );
}

interface FormCardProps {
  form: FormListItemView;
  onOpen: () => void;
  menu?: ReactNode;
}

export function FormCard({ form, onOpen, menu }: FormCardProps) {
  const rail = FORM_STATUS_META[form.statusID]?.color ?? 'gray';
  const visual = formVisual(form);
  const closing = form.statusID === 2 ? closingInfo(form.closesAt) : null;
  const meta = [form.categoryName, form.departmentName, form.locationName].filter(Boolean).join(' · ');

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      onClick={onOpen}
      style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}
    >
      {/* Status rail (a strip, not a border — the theme forces card border colours). */}
      <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, backgroundColor: `var(--mantine-color-${rail}-6)` }} />
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
          <ThemeIcon size={42} radius="md" variant="light" color={visual.color} style={{ flexShrink: 0 }}>
            {visual.icon}
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={700} lineClamp={2} lh={1.3}>
              {form.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {meta || (form.ownerName ? `Owned by ${form.ownerName}` : 'No category')}
            </Text>
          </Stack>
        </Group>
        {menu && (
          <Box onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <Menu position="bottom-end" withinPortal shadow="md">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label="Form actions">
                  <IconDotsVertical size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>{menu}</Menu.Dropdown>
            </Menu>
          </Box>
        )}
      </Group>

      <Group gap={6} mt="md" wrap="wrap">
        <FormStatusBadge statusId={form.statusID} name={form.statusName} size="md" />
        {form.hasUnpublishedChanges && form.publishedVersionNo > 0 && (
          <Tooltip label="The builder has changes that are not live yet" withArrow>
            <Badge size="md" variant="light" color="yellow" leftSection={<IconAlertTriangle size={12} />} styles={{ root: { textTransform: 'none' } }}>
              Unpublished changes
            </Badge>
          </Tooltip>
        )}
        {!form.isPublic && form.statusID === 2 && (
          <Tooltip label="The public link is off — entries arrive through the API or inbound tokens" withArrow>
            <Badge size="md" variant="light" color="gray" leftSection={<IconLock size={12} />} styles={{ root: { textTransform: 'none' } }}>
              API only
            </Badge>
          </Tooltip>
        )}
      </Group>

      <SimpleGrid cols={3} mt="md" spacing="xs">
        <Count label="Entries" value={form.entryCount} />
        <Count label="To approve" value={form.pendingApprovalCount} color="yellow.7" />
        <Count label="Waitlist" value={form.waitlistCount} color="grape" />
      </SimpleGrid>

      <Box style={{ flex: 1 }} />

      <Group justify="space-between" mt="md" pt="sm" wrap="nowrap" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        {closing ? (
          <Group gap={4} wrap="nowrap">
            <IconClock size={14} color={`var(--mantine-color-${closing.color === 'gray' ? 'dimmed' : `${closing.color}-6`})`} />
            <Text size="xs" c={closing.color === 'gray' ? 'dimmed' : closing.color} fw={closing.color === 'gray' ? 400 : 600}>
              {closing.label}
            </Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            {form.statusID === 1 ? 'Not published yet' : form.statusID === 2 ? 'No closing date' : FORM_STATUS_META[form.statusID]?.label ?? ''}
          </Text>
        )}
        {form.shortCode && <RefChip value={form.shortCode} />}
      </Group>
    </Card>
  );
}
